import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useTenant } from "@/context/tenant-context";
import { appsApi, type CreateAppInput } from "@/api/apps";
import { appTemplatesApi } from "@/api/app-templates";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { queryKeys } from "@/lib/query-keys";
import {
  AppWindow,
  Plus,
  Trash2,
  ArrowRight,
  Code2,
  Globe,
  Lock,
  X,
  LayoutTemplate,
  Download,
  Bot,
  Rocket,
  MessageSquare,
} from "lucide-react";

const statusVariant: Record<
  string,
  "success" | "warning" | "danger" | "default" | "secondary"
> = {
  deployed: "success",
  building: "warning",
  draft: "secondary",
  failed: "danger",
  stopped: "default",
};

const visibilityIcon: Record<string, typeof Globe> = {
  public: Globe,
  internal: Lock,
  "agents-only": Bot,
};

type AppsProps = {
  embedded?: boolean;
  onOpenApp?: (appId: string) => void;
  onOpenBuilder?: (appId: string) => void;
};

export function Apps({ onOpenApp, onOpenBuilder }: AppsProps = {}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [framework, setFramework] = useState("");

  const { data: allAgents = [] } = useAgentsByCompany(tenantId);
  const appBuilderAgent = allAgents.find((a) => a.role === "app-builder");
  const openApp = (appId: string) => {
    if (onOpenApp) {
      onOpenApp(appId);
      return;
    }
    navigate(`/tenants/${tenantId}/apps/${appId}`);
  };
  const openBuilder = (appId: string) => {
    if (onOpenBuilder) {
      onOpenBuilder(appId);
      return;
    }
    navigate(`/tenants/${tenantId}/apps/${appId}/builder`);
  };

  const { data: apps = [], isLoading } = useQuery({
    queryKey: queryKeys.apps.byCompany(tenantId),
    queryFn: () => appsApi.list(tenantId),
  });

  const { data: templates = [] } = useQuery({
    queryKey: queryKeys.appTemplates.all,
    queryFn: () => appTemplatesApi.list(),
  });

  const activateMutation = useMutation({
    mutationFn: () => appsApi.activate(tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.byCompany(tenantId) });
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateAppInput) => appsApi.create(data),
    onSuccess: (app) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.apps.byCompany(tenantId),
      });
      setShowCreate(false);
      setName("");
      setDescription("");
      setFramework("");
      openBuilder(app.id);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => appsApi.delete(id),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.apps.byCompany(tenantId),
      }),
  });

  return (
    <CenterPanel title="Apps">
      {/* App Builder agent status */}
      {!appBuilderAgent ? (
        <Card className="mb-6 border-accent/30 bg-accent/5">
          <CardContent className="flex items-center justify-between py-5">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent/10">
                <Rocket size={24} className="text-accent" />
              </div>
              <div>
                <p className="text-sm font-semibold text-text-primary">
                  Activate App Builder
                </p>
                <p className="text-xs text-text-secondary">
                  Deploy a dedicated AI coding agent that builds and deploys web apps into your Azure infrastructure.
                </p>
              </div>
            </div>
            <Button
              onClick={() => activateMutation.mutate()}
              disabled={activateMutation.isPending}
            >
              <Bot size={16} className="mr-1.5" />
              {activateMutation.isPending ? "Activating..." : "Activate Apps"}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="mb-6">
          <CardContent className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              <Bot size={18} className="text-accent" />
              <div>
                <p className="text-sm font-medium text-text-primary">
                  App Builder Agent
                </p>
                <p className="text-xs text-text-muted">
                  {appBuilderAgent.status === "active"
                    ? "Online — chat to request new apps"
                    : `Status: ${appBuilderAgent.status}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge
                variant={appBuilderAgent.status === "active" ? "success" : "warning"}
              >
                {appBuilderAgent.status}
              </Badge>
              <Button
                size="sm"
                onClick={() => setShowCreate(true)}
              >
                <Rocket size={14} className="mr-1.5" />
                Start Building
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-text-secondary">
          {apps.length} app{apps.length !== 1 ? "s" : ""}
        </p>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <Plus size={16} className="mr-1.5" />
          New App
        </Button>
      </div>

      {/* Create form */}
      {showCreate && (
        <Card className="mb-4">
          <CardContent className="py-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm font-medium text-text-primary">
                Register a new app
              </p>
              <button
                onClick={() => setShowCreate(false)}
                className="text-text-muted hover:text-text-primary"
              >
                <X size={16} />
              </button>
            </div>
            <div className="flex flex-col gap-3">
              <Input
                placeholder="App name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <Input
                placeholder="Description (optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <Input
                placeholder="Framework (e.g. next, react, express)"
                value={framework}
                onChange={(e) => setFramework(e.target.value)}
              />
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCreate(false)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={!name.trim() || createMutation.isPending}
                  onClick={() =>
                    createMutation.mutate({
                      companyId: tenantId,
                      name: name.trim(),
                      description: description.trim() || undefined,
                      framework: framework.trim() || undefined,
                    })
                  }
                >
                  {createMutation.isPending ? "Creating..." : "Create App"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <p className="text-sm text-text-muted">Loading apps...</p>
      ) : apps.length === 0 && !showCreate ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <AppWindow size={40} className="mb-3 text-text-muted" />
            <p className="mb-1 text-sm font-medium text-text-primary">
              No apps yet
            </p>
            <p className="mb-4 text-xs text-text-muted">
              Apps are built by your App Builder agent and deployed to Azure App
              Service within your tenant&apos;s infrastructure.
            </p>
            <Button size="sm" onClick={() => setShowCreate(true)}>
              <Plus size={14} className="mr-1.5" />
              Register your first app
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {apps.map((app) => {
            const VisIcon = visibilityIcon[app.visibility] || Lock;
            return (
              <Card
                key={app.id}
                className="cursor-pointer transition-colors hover:border-accent/40"
                onClick={() => openApp(app.id)}
              >
                <CardContent className="flex items-center justify-between py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10">
                      <AppWindow size={20} className="text-accent" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-text-primary">
                        {app.name}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-text-muted">
                        {app.description && (
                          <span className="max-w-[300px] truncate">
                            {app.description}
                          </span>
                        )}
                        {app.framework && (
                          <Badge variant="outline">{app.framework}</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5 text-xs text-text-muted">
                      <VisIcon size={14} />
                      {app.visibility}
                    </div>
                    <Badge variant={statusVariant[app.status] ?? "default"}>
                      {app.status}
                    </Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        openBuilder(app.id);
                      }}
                    >
                      <Code2 size={14} className="mr-1.5" />
                      Builder
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm(`Delete app "${app.name}"?`)) {
                          deleteMutation.mutate(app.id);
                        }
                      }}
                    >
                      <Trash2 size={14} />
                    </Button>
                    <ArrowRight size={16} className="text-text-muted" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      {/* Template marketplace */}
      {templates.length > 0 && (
        <div className="mt-8">
          <div className="mb-3 flex items-center gap-2">
            <LayoutTemplate size={16} className="text-text-secondary" />
            <h2 className="text-sm font-medium text-text-secondary">
              App Templates
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {templates.map((tpl) => (
              <Card
                key={tpl.id}
                className="transition-colors hover:border-accent/40"
              >
                <CardContent className="py-4">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-sm font-medium text-text-primary">
                      {tpl.name}
                    </p>
                    <Badge variant="outline">{tpl.category}</Badge>
                  </div>
                  {tpl.description && (
                    <p className="mb-3 text-xs text-text-muted line-clamp-2">
                      {tpl.description}
                    </p>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-text-muted">
                      {tpl.framework && <Badge variant="secondary">{tpl.framework}</Badge>}
                      <span>
                        <Download size={12} className="mr-0.5 inline" />
                        {tpl.installCount}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setName(tpl.name);
                        setDescription(tpl.description || "");
                        setFramework(tpl.framework || "");
                        setShowCreate(true);
                      }}
                    >
                      Use template
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </CenterPanel>
  );
}
